import { useLocation } from "react-router-dom";
import MetricValuesChart from "components-for/metrics/metric-values-chart";
import MetricComparisonChart from "components-for/metrics/metric-comparison-chart";
import { faCalculator } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FunctionComponent, memo } from "react";
import { useFirstMetricAvailable } from "hooks-for/metrics/use-first-available-metric";
import { IProps } from "./metrics-over-time-charts.types";
import { UrlForm, useQueryParameters } from "@edgetier/client-components";
import { SelectEnum } from "@edgetier/select";
import { useFilterAvailableMetrics } from "hooks-for/metrics/use-filter-available-metrics";
import { INTERACTIONS_TABLE_PREFIX } from "constants/interactions";
import { parseUrlArray } from "@edgetier/utilities";
import "./metrics-over-time-charts.scss";

/**
 * Show two metrics charts with a metric select above them.
 * @param props.defaultEndDateTime   Closing date in range to search.
 * @param props.defaultStartDateTime Opening date in range to search.
 * @param props.excludedInteractionMetrics List of excluded interaction metrics
 * @returns                          Select and charts.
 */
const MetricsOverTimeCharts: FunctionComponent<IProps> = ({
    defaultEndDateTime,
    defaultStartDateTime,
    excludedInteractionMetrics,
}) => {
    const { search } = useLocation();
    const firstMetricAvailable = useFirstMetricAvailable(search);

    const { comparisonAgentId, reviewAgentId } = useQueryParameters({
        comparisonAgentId: parseUrlArray,
        reviewAgentId: String,
    });

    const metricsAvailable = useFilterAvailableMetrics(excludedInteractionMetrics);

    return (
        <div className="metrics-over-time-charts">
            <UrlForm initialValues={{ metric: firstMetricAvailable }} parameterPrefix={INTERACTIONS_TABLE_PREFIX}>
                <div className="field field-inline">
                    <div className="field-inline__icon">
                        <FontAwesomeIcon icon={faCalculator} />
                    </div>
                    <SelectEnum
                        isClearable={false}
                        name="metric"
                        items={metricsAvailable}
                        description="metric"
                        isFilter={false}
                        isSingleSelect
                    />
                </div>
            </UrlForm>

            <div className="metrics-over-time-charts__charts">
                <MetricComparisonChart
                    defaultEndDateTime={defaultEndDateTime}
                    defaultStartDateTime={defaultStartDateTime}
                    metric={firstMetricAvailable}
                    reviewAgentId={reviewAgentId}
                    comparisonAgentId={comparisonAgentId}
                />

                <MetricValuesChart
                    defaultEndDateTime={defaultEndDateTime}
                    defaultStartDateTime={defaultStartDateTime}
                    metric={firstMetricAvailable}
                    reviewAgentId={reviewAgentId}
                />
            </div>
        </div>
    );
};

export default memo(MetricsOverTimeCharts);
